import { jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import {
  dealSchema,
  dealsPageSchema,
  dealStatusCountsSchema,
  serializeDealLookupFilter,
  serializeDealsFilter,
  type DealLookupFilter,
  type DealIntegrationLookupFilter,
  type DealMutationPayload,
  type DealsFilter,
} from './model';
import { integrationsPageSchema } from '../integrations/model';
import { targetingBuildersPageSchema } from '../../targeting/builder/model';

interface DealsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const dealMutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === null ? undefined : value),
  z.union([z.void(), dealSchema]),
);

export function createDealsClient({ config, accessToken, fetcher }: DealsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    list: (filter: DealsFilter) =>
      api.request({
        path: `/api/deals?${serializeDealsFilter(filter)}`,
        schema: dealsPageSchema,
      }),
    getStatusCounts: (searchTerm: string) =>
      api.request({
        path: `/api/deals/statuses?${toSearchParams({ name: searchTerm.trim() })}`,
        schema: dealStatusCountsSchema,
      }),
    get: (id: string) =>
      api.request({
        path: `/api/deals/${encodeURIComponent(id)}`,
        schema: dealSchema,
      }),
    create: (payload: DealMutationPayload) =>
      api.request({
        path: '/api/deals',
        schema: dealMutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    update: (id: string, payload: DealMutationPayload) =>
      api.request({
        path: `/api/deals/${encodeURIComponent(id)}`,
        schema: dealMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    searchTargetings: (filter: DealLookupFilter) =>
      api.request({
        path: `/api/targetings?${serializeDealLookupFilter(filter)}`,
        schema: targetingBuildersPageSchema,
      }),
    searchIntegrations: (filter: DealIntegrationLookupFilter) => {
      const params = serializeDealLookupFilter(filter);
      params.delete('searchTerm');
      if (filter.searchTerm.trim()) params.set('name', filter.searchTerm.trim());
      if (filter.statuses.length) params.set('status', filter.statuses.join(','));
      return api.request({
        path: `/api/dps/integrations?${params}`,
        schema: integrationsPageSchema,
      });
    },
  };
}

export type DealsClient = ReturnType<typeof createDealsClient>;
